// Estado da pausa em relação ao limite do motivo (motivos de pausa com limite).
// Puro e testado: o banner só desenha o que isto decide.

export type BreakPhase = "livre" | "dentro" | "perto" | "excedida";

export interface BreakLimitState {
  phase: BreakPhase;
  /** Segundos até o limite (0 depois dele). */
  restanteSeg: number;
  /** Segundos além do limite (0 antes dele). */
  excedidoSeg: number;
  /** 0..1 — preenchimento da barra de progresso. */
  fracao: number;
}

/** "Perto do fim" = faltando até 2 min, ou 20% do limite se ele for curto. */
const AVISO_MAX_SEG = 120;
const AVISO_FRACAO = 0.2;

/**
 * `maxSeconds` `null` = motivo sem limite; `undefined` = ninguém resolveu o
 * limite (pausa vinda de outra sessão, API anterior). Os dois são pausa livre
 * na tela: sem limite conhecido, não há o que avisar — nunca inventar um.
 */
export function breakLimit(elapsedSeg: number, maxSeconds: number | null | undefined): BreakLimitState {
  if (maxSeconds == null || maxSeconds <= 0) {
    return { phase: "livre", restanteSeg: 0, excedidoSeg: 0, fracao: 0 };
  }
  const restante = maxSeconds - elapsedSeg;
  // Estritamente MAIOR, como no relatório: terminar cravado no limite não é estouro.
  if (restante < 0) {
    return { phase: "excedida", restanteSeg: 0, excedidoSeg: -restante, fracao: 1 };
  }
  const aviso = Math.min(AVISO_MAX_SEG, maxSeconds * AVISO_FRACAO);
  return {
    phase: restante <= aviso ? "perto" : "dentro",
    restanteSeg: restante,
    excedidoSeg: 0,
    fracao: elapsedSeg / maxSeconds,
  };
}

/** `07:12` / `1:02:05` — mesmo desenho do contador da pausa. */
export function formatClock(seg: number): string {
  const s = Math.max(0, Math.floor(seg));
  const h = Math.floor(s / 3600);
  const mm = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

/** "7 min e 12 s além do limite". */
export function formatOverrunLong(seg: number): string {
  const s = Math.max(0, Math.floor(seg));
  const m = Math.floor(s / 60);
  const r = s % 60;
  const partes = m > 0 ? (r > 0 ? `${m} min e ${r} s` : `${m} min`) : `${r} s`;
  return `${partes} além do limite`;
}

/** "60 min" — o limite como o ISP o configurou (minutos inteiros na tela de cadastro). */
export function formatLimitMinutes(maxSeconds: number): string {
  return `${Math.round(maxSeconds / 60)} min`;
}
